import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, Trash2, History, ImageIcon } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import api from "@/lib/api";
import { absoluteMediaUrl } from "@/components/ImageField";

const META_KEYS = ["alt", "focal_x", "focal_y", "zoom"];

/**
 * Pannello admin riusabile per le immagini di sezione con punto focale X/Y + zoom.
 * Salvataggio immediato: l'upload pubblica subito, i metadati si salvano con "Salva".
 * Istanze: home_stp (sezione homepage), shop_hero (pagina Shop), stp_page (pagina Shop the Project).
 */
export function SectionImagePanel({ section, title, note }) {
  const [s, setS] = useState(null);
  const [orig, setOrig] = useState(null);
  const [busy, setBusy] = useState(false);
  const deskRef = useRef(null);
  const mobRef = useRef(null);

  const K = (k) => `${section}_${k}`;
  const metaOf = (src) => Object.fromEntries(META_KEYS.map((k) => [
    K(k),
    k === "alt" ? (src?.[K(k)] || "") : Number(src?.[K(k)] ?? (k === "zoom" ? 100 : 50)),
  ]));

  const load = async () => {
    try {
      const { data } = await api.get("/settings");
      setS(data); setOrig(data);
    } catch { toast.error("Errore nel caricamento delle impostazioni"); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!s) return <p className="text-sm text-taupe">Caricamento…</p>;

  const upload = async (target, file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/admin/section-image/${section}?target=${target}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(target === "desktop" ? "Immagine aggiornata sul sito" : "Immagine mobile aggiornata sul sito");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore caricamento");
    } finally { setBusy(false); }
  };

  const saveMeta = async () => {
    setBusy(true);
    try {
      await api.put("/admin/settings", metaOf(s));
      toast.success("Impostazioni immagine salvate");
      load();
    } catch { toast.error("Errore salvataggio"); }
    finally { setBusy(false); }
  };

  const cancel = () => { setS(orig); toast.info("Modifiche annullate"); };

  const restore = async () => {
    setBusy(true);
    try {
      await api.post(`/admin/section-image/${section}/restore`);
      toast.success("Immagine precedente ripristinata");
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Nessuna immagine precedente"); }
    finally { setBusy(false); }
  };

  const remove = async (target) => {
    const msg = target === "mobile"
      ? "Eliminare la versione mobile? Verrà usata l'immagine principale."
      : "Eliminare l'immagine? Tornerà quella predefinita del sito.";
    if (!window.confirm(msg)) return;
    setBusy(true);
    try {
      await api.delete(`/admin/section-image/${section}?target=${target}`);
      toast.success("Immagine rimossa");
      load();
    } catch { toast.error("Errore eliminazione"); }
    finally { setBusy(false); }
  };

  const img = s[K("image")] || "";
  const imgMobile = s[K("image_mobile")] || "";
  const focal = `${Number(s[K("focal_x")] ?? 50)}% ${Number(s[K("focal_y")] ?? 50)}%`;
  const zoom = Math.min(200, Math.max(100, Number(s[K("zoom")]) || 100)) / 100;
  const dirty = JSON.stringify(metaOf(s)) !== JSON.stringify(metaOf(orig));
  const hasPrev = !!(s[K("image_prev")] || s[K("image_mobile_prev")]);
  const btn = "min-h-[44px] inline-flex items-center gap-2 text-xs tracking-[0.14em] uppercase px-4 py-2 transition-colors disabled:opacity-60";

  const Preview = ({ label, url, mobile, tid }) => (
    <div data-testid={tid}>
      <span className="text-[10px] tracking-[0.14em] uppercase text-taupe">{label}</span>
      <div className={`mt-2 relative overflow-hidden border border-hair bg-bone ${mobile ? "h-64 w-40" : "h-44 w-full"}`}>
        {url ? (
          <img src={absoluteMediaUrl(url)} alt={s[K("alt")] || "Anteprima immagine"} className="w-full h-full object-cover"
            style={{ objectPosition: focal, transform: zoom > 1 ? `scale(${zoom})` : "none" }} />
        ) : (
          <div className="w-full h-full flex flex-col items-center justify-center text-taupe gap-2">
            <ImageIcon size={20} />
            <span className="text-xs text-center px-2">{mobile ? "Come desktop" : "Immagine predefinita del sito"}</span>
          </div>
        )}
      </div>
    </div>
  );

  return (
    <section className="bg-white border border-hair p-5 md:p-8" data-testid={`section-image-${section}`}>
      <div className="eyebrow">{title}</div>
      <p className="text-xs text-taupe mt-3">
        {note || "Formati: JPG, JPEG, PNG, WEBP · max 10 MB. Le versioni responsive vengono generate automaticamente; l'originale resta invariato. Il punto focale e lo zoom evitano tagli indesiderati su schermi diversi."}
      </p>

      <div className="grid md:grid-cols-[1fr_auto] gap-6 mt-6 items-start">
        <Preview label="Anteprima desktop" url={img} tid={`section-image-${section}-preview-desktop`} />
        <Preview label="Anteprima mobile" url={imgMobile || img} mobile tid={`section-image-${section}-preview-mobile`} />
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <button onClick={() => deskRef.current?.click()} disabled={busy} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid={`section-image-${section}-upload-desktop`}>
          <Upload size={14} /> {img ? "Sostituisci immagine" : "Carica immagine"}
        </button>
        <button onClick={() => mobRef.current?.click()} disabled={busy} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid={`section-image-${section}-upload-mobile`}>
          <Upload size={14} /> Immagine mobile (opzionale)
        </button>
        {hasPrev && (
          <button onClick={restore} disabled={busy} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid={`section-image-${section}-restore`}>
            <History size={14} /> Ripristina precedente
          </button>
        )}
        {img && (
          <button onClick={() => remove("desktop")} disabled={busy} className={`${btn} border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid={`section-image-${section}-delete-desktop`}>
            <Trash2 size={14} /> Elimina
          </button>
        )}
        {imgMobile && (
          <button onClick={() => remove("mobile")} disabled={busy} className={`${btn} border border-red-700 text-red-700 hover:bg-red-700 hover:text-bone`} data-testid={`section-image-${section}-delete-mobile`}>
            <Trash2 size={14} /> Elimina mobile
          </button>
        )}
      </div>
      <input ref={deskRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid={`section-image-${section}-file-desktop`}
        onChange={(e) => { upload("desktop", e.target.files?.[0]); e.target.value = ""; }} />
      <input ref={mobRef} type="file" accept=".jpg,.jpeg,.png,.webp" className="hidden" data-testid={`section-image-${section}-file-mobile`}
        onChange={(e) => { upload("mobile", e.target.files?.[0]); e.target.value = ""; }} />

      <div className="grid md:grid-cols-2 gap-6 mt-6">
        <div className="md:col-span-2">
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Testo alternativo (accessibilità e SEO)</Label>
          <Input value={s[K("alt")] || ""} onChange={(e) => setS({ ...s, [K("alt")]: e.target.value })} className="rounded-none mt-1"
            placeholder="Es. Soggiorno progettato dallo Studio con arredi selezionati" data-testid={`section-image-${section}-alt`} />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Punto focale orizzontale: {Number(s[K("focal_x")] ?? 50)}%</Label>
          <input type="range" min={0} max={100} step={5} value={Number(s[K("focal_x")] ?? 50)}
            onChange={(e) => setS({ ...s, [K("focal_x")]: Number(e.target.value) })}
            className="w-full mt-4 accent-[#9E8462]" data-testid={`section-image-${section}-focal-x`} />
        </div>
        <div>
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Punto focale verticale: {Number(s[K("focal_y")] ?? 50)}%</Label>
          <input type="range" min={0} max={100} step={5} value={Number(s[K("focal_y")] ?? 50)}
            onChange={(e) => setS({ ...s, [K("focal_y")]: Number(e.target.value) })}
            className="w-full mt-4 accent-[#9E8462]" data-testid={`section-image-${section}-focal-y`} />
          <p className="text-[11px] text-taupe mt-1">0% = alto, 100% = basso.</p>
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] tracking-[0.14em] uppercase text-taupe">Zoom / ritaglio: {Number(s[K("zoom")] ?? 100)}%</Label>
          <input type="range" min={100} max={200} step={5} value={Number(s[K("zoom")] ?? 100)}
            onChange={(e) => setS({ ...s, [K("zoom")]: Number(e.target.value) })}
            className="w-full mt-4 accent-[#9E8462]" data-testid={`section-image-${section}-zoom`} />
          <p className="text-[11px] text-taupe mt-1">Aumenta per riposizionare e ritagliare senza deformare l'immagine.</p>
        </div>
      </div>

      <div className="flex gap-2 mt-6">
        <button onClick={saveMeta} disabled={busy || !dirty} className={`${btn} bg-ink text-bone hover:bg-gold`} data-testid={`section-image-${section}-save`}>Salva</button>
        <button onClick={cancel} disabled={busy || !dirty} className={`${btn} border border-ink hover:bg-ink hover:text-bone`} data-testid={`section-image-${section}-cancel`}>Annulla</button>
      </div>
    </section>
  );
}
